import { z } from 'zod';

/**
 * Money is stored as DECIMAL server-side (never float — see session notes §10).
 * It arrives over the wire as a string to preserve precision; we keep it a
 * string and expose helpers for display/maths rather than coercing to number.
 */
export const moneySchema = z.union([z.string(), z.number()]).transform((v) => String(v));
export type Money = z.infer<typeof moneySchema>;

export const isoDateTimeSchema = z.string();
export const idSchema = z.union([z.string(), z.number()]).transform((v) => String(v));

/** All Alpha One payouts are denominated in LDS Token. */
export const currencySchema = z.enum(['LDS', 'USD', 'USDT']);
export type Currency = z.infer<typeof currencySchema>;

export const approvalStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

/** Laravel length-aware paginator shape. */
export const paginationMetaSchema = z.object({
  current_page: z.number(),
  from: z.number().nullable(),
  to: z.number().nullable(),
  per_page: z.number(),
  last_page: z.number(),
  total: z.number(),
});
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    meta: paginationMetaSchema,
  });
}

export type Paginated<T> = { data: T[]; meta: PaginationMeta };

/** Envelope used by single-resource endpoints. */
export function resource<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: item });
}

/**
 * Query params every server-mode table sends. Per §6.4 the source app filters
 * server-side via URL params — TanStack Table runs in manual mode and mirrors
 * these onto the URL.
 */
export interface ListQuery {
  page?: number;
  per_page?: number;
  q?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  from?: string;
  to?: string;
  [key: string]: unknown;
}

export const apiErrorSchema = z.object({
  message: z.string(),
  errors: z.record(z.array(z.string())).optional(),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;
